import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { brasilApi } from "../../service/brasilApi";
import api from "../../service/api";
import {
  AuthLayout,
  authCardClass,
  authLinkClass,
  authIconClass,
  authTitleClass,
  authSubtitleClass,
} from "../../components/auth-layout";
import { AuthBrand, AuthMobileLogoShell } from "../../components/auth-brand";
import { Button } from "../../components/Button";
import * as Input from "../../components/Form/Input";
import { PasswordInput } from "../../components/password-input";
import { ArrowLeft, Building2, FileText, Mail } from "lucide-react";

export default function Cadastro() {
  const navigate = useNavigate();

  const [typeDocument, setTypeDocument] = useState("cnpj");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [name, setName] = useState("");
  const [cpfOrCnpj, setCpfOrCnpj] = useState("");
  const [accptTerms, setAcceptTerms] = useState(false);
  const [sector, setSector] = useState("");

  function handleTypeDocument(type: string) {
    setTypeDocument(type);
    if (type === "cnpj") {
      setSector("");
    }
  }

  const sectorOk =
    typeDocument === "cpf" ||
    (typeDocument === "cnpj" && (sector === "PRIVATE" || sector === "PUBLIC"));

  const formComplete =
    !!email.trim() &&
    !!password &&
    !!passwordConfirm &&
    !!name.trim() &&
    !!cpfOrCnpj.trim() &&
    accptTerms &&
    sectorOk;

  function handleAcceptTerms() {
    setAcceptTerms(!accptTerms);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (
      !email.trim() ||
      !password ||
      !passwordConfirm ||
      !name.trim() ||
      !cpfOrCnpj.trim() ||
      !accptTerms
    ) {
      toast.info("Preencha todos os campos");
      return;
    }

    if (typeDocument === "cnpj" && !sectorOk) {
      toast.info("Selecione o setor");
      return;
    }

    if (password !== passwordConfirm) {
      toast.info("As senhas precisam ser iguais");
      return;
    }

    const sectorPayload = typeDocument === "cnpj" ? sector : "PUBLIC";

    await api
      .post("/users", {
        name,
        email,
        password,
        document: cpfOrCnpj,
        sector: sectorPayload,
        user_type: typeDocument === "cnpj" ? "USERENTERPRISE" : "USER",
      })
      .then(() => {
        toast.success("Conta criada com sucesso");
        navigate("/");
      })
      .catch((error) => {
        const data = error?.response?.data;
        const msg =
          (typeof data?.message === "string" && data.message) ||
          (typeof data?.error === "string" && data.error) ||
          "Erro ao criar conta";
        toast.error(msg);
      });
  }

  async function getName(cnpj: string) {
    try {
      const { data } = await brasilApi.get(`/cnpj/v1/${cnpj}`);
      setName(data.razao_social);
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    if (typeDocument === "cnpj" && cpfOrCnpj.length) {
      getName(cpfOrCnpj);
    }
  }, [cpfOrCnpj, typeDocument]);

  return (
    <AuthLayout>
      <AuthMobileLogoShell>
        <AuthBrand className="h-10 w-auto max-w-[min(100%,240px)] object-contain" />
      </AuthMobileLogoShell>

      <div className={authCardClass}>

        <div className="mb-6 text-center">
          <h1 className={authTitleClass}>Criar conta</h1>
          <p className={authSubtitleClass}>
            Preencha os dados para se cadastrar no painel
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-xs font-medium text-red-600">
            (*) Campos obrigatórios
          </p>

          <div>
            <span className="mb-2 block text-sm font-medium text-zinc-800">
              Tipo de documento
            </span>
            <div className="flex flex-wrap gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 text-sm text-zinc-800 shadow-sm transition-colors has-[:checked]:border-apcap/50 has-[:checked]:bg-apcap-muted has-[:checked]:ring-2 has-[:checked]:ring-apcap/15">
                <input
                  type="radio"
                  name="document"
                  value="cnpj"
                  defaultChecked
                  onChange={() => handleTypeDocument("cnpj")}
                  className="text-apcap focus:ring-apcap"
                />
                CNPJ
              </label>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 text-sm text-zinc-800 shadow-sm transition-colors has-[:checked]:border-apcap/50 has-[:checked]:bg-apcap-muted has-[:checked]:ring-2 has-[:checked]:ring-apcap/15">
                <input
                  type="radio"
                  name="document"
                  value="cpf"
                  onChange={() => handleTypeDocument("cpf")}
                  className="text-apcap focus:ring-apcap"
                />
                CPF
              </label>
            </div>
          </div>

          {typeDocument === "cnpj" && (
            <div>
              <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-zinc-800">
                <Building2 className={`h-4 w-4 ${authIconClass}`} />
                Setor <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={sector}
                onChange={(e) => setSector(e.target.value)}
                className="block w-full rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 text-sm text-zinc-900 shadow-sm outline-none transition-colors focus:border-apcap/50 focus:shadow-[0_0_0_3px_rgba(1,88,50,0.12)]"
              >
                <option value="" disabled>
                  Selecione o setor
                </option>
                <option value="PRIVATE">Privado</option>
                <option value="PUBLIC">Público</option>
              </select>
            </div>
          )}

          <div>
            <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-zinc-800">
              <FileText className={`h-4 w-4 ${authIconClass}`} />
              {typeDocument === "cnpj" ? "CNPJ" : "CPF"}
            </label>
            <Input.Root variant="auth">
              <Input.Control
                type="text"
                placeholder="Somente números"
                value={cpfOrCnpj}
                maxLength={14}
                onChange={(e) => setCpfOrCnpj(e.target.value)}
              />
            </Input.Root>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-zinc-800">
              Nome {typeDocument === "cnpj" ? "(preenchido via CNPJ)" : ""}
            </label>
            <Input.Root
              variant="auth"
              className={
                typeDocument === "cnpj" ? "bg-zinc-50" : undefined
              }
            >
              <Input.Control
                type="text"
                placeholder="Nome completo ou razão social"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={typeDocument === "cnpj"}
                className={
                  typeDocument === "cnpj"
                    ? "cursor-default text-zinc-700"
                    : undefined
                }
              />
            </Input.Root>
            {typeDocument === "cnpj" ? (
              <p className="mt-1 text-xs text-zinc-500">
                Razão social carregada automaticamente a partir do CNPJ.
              </p>
            ) : null}
          </div>

          <div>
            <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-zinc-800">
              <Mail className={`h-4 w-4 ${authIconClass}`} />
              E-mail
            </label>
            <Input.Root variant="auth">
              <Input.Control
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Input.Root>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-zinc-800">
              Senha
            </label>
            <PasswordInput
              variant="auth"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-zinc-800">
              Confirmar senha
            </label>
            <PasswordInput
              variant="auth"
              autoComplete="new-password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
            />
          </div>

          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-zinc-200 bg-zinc-50 p-3.5 transition-colors hover:border-zinc-300">
            <input
              type="checkbox"
              checked={accptTerms}
              onChange={handleAcceptTerms}
              className="mt-0.5 rounded border-zinc-300 text-apcap focus:ring-apcap"
            />
            <span className="text-sm text-zinc-600">
              Aceitar termos <span className="text-red-500">*</span>
            </span>
          </label>

          <div className="max-h-40 overflow-y-auto rounded-xl border border-apcap/20 bg-apcap-muted/50 p-4 text-xs leading-relaxed text-zinc-700 scrollbar-thin">
            <h2 className="font-semibold text-apcap-deep">
              Termos de uso
            </h2>
            <p className="mt-2">
              Concordo com o tratamento dos meus dados pessoais pela Aprender e
              Capacitar Brasil para a finalidade de criação de conta de usuário,
              envio de newsletters e certificados. Entendo que estou informando
              dados que incluem informações como nome, endereço de e-mail e
              CNPJ, e que esses dados serão armazenados pela empresa pelo
              período necessário para alcançar a finalidade do tratamento. Fui
              informado(a) de que posso revogar este consentimento a qualquer
              momento, entrando em contato com a empresa através do e-mail
              aprendercapacitarbrasil@gmail.com.
            </p>
          </div>

          <Button
            type="submit"
            variant="brand"
            className="h-11 w-full font-medium"
            disabled={!formComplete}
          >
            Cadastrar
          </Button>

          <Button
            type="button"
            variant="brandOutline"
            className="h-11 w-full font-medium"
            onClick={() => navigate("/")}
          >
            Cancelar
          </Button>
        </form>

        <Link
          to="/"
          className={`mt-6 flex items-center justify-center gap-2 text-sm ${authLinkClass}`}
        >
          <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
          Já tenho conta
        </Link>
      </div>
    </AuthLayout>
  );
}
